import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, inject, DestroyRef, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Configuration } from 'src/app/models/configurations/configuration';
import { ConfigurationApplicationStatus, ConfigurationPackageStatus, ConfigurationState }
  from 'src/app/models/configurations/configuration-state';
import { Constants } from 'src/app/models/constants';
import { KeyValueMap } from 'src/app/models/key-value-map';
import { Sort, SortDirection } from 'src/app/models/sort';
import { CRVTService } from 'src/app/services/api/crvt.service';
import { UrlService } from 'src/app/services/util/url.service';
import { PaginatorComponent } from '../../../components/paginator/paginator.component';
import { isDate } from '../../../services/util/utils';
import { ConfigurationsService } from 'src/app/services/api/configurations.service';
import { Permission } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { MatDialogRef, MAT_DIALOG_DATA,MatDialog } from '@angular/material/dialog';
import { ErrorDialogJarComponent } from 'src/app/components/error-dialog-jar/error-dialog-jar.component';
import { ConfigurationDetailsComponent } from '../configuration-details/configuration-details.component';

type ComparatorFunc = (first: any, second: any) => number;

const REQUIRED_PERMISSIONS: Permission[] = [
  Permission.APPLY_RELEASE_PACKAGE_HOTFIX
];

@Component({
    selector: 'app-view-update-results',
    templateUrl: './view-update-results.component.html',
    styleUrls: ['./view-update-results.component.scss'],
    standalone: false
})
export class ViewUpdateResultsComponent implements OnInit {
  @ViewChild(PaginatorComponent, { static: true }) paginator: PaginatorComponent;
private destroyRef = inject(DestroyRef);
  @Input() title: string;
  @Input() subtitle: string;
  @Input() blueprintId: string;
  @Input() configurationStates: ConfigurationState[];
  @Input()
  set configurations(configurations: Configuration[]) {
    this.configurationsMap = configurations.reduce((acc, configuration) => {
      acc[configuration.id] = configuration;
      return acc;
    }, {});
  }
  @Input() viewAllResults = false;

  pageConfigurationStates: ConfigurationState[];
  filteredConfigurationStates: ConfigurationState[];

  Constants = Constants;
  ConfigurationApplicationStatus = ConfigurationApplicationStatus;
  ConfigurationPackageStatus = ConfigurationPackageStatus;

  configurationsMap: KeyValueMap<string, Configuration>;
  expandedRows: KeyValueMap<string, boolean> = {};
  searchTerm: string;

  SortDirection = SortDirection;
  sort: Sort;
  permissionsMap: { [key: string]: boolean };
  Permission = Permission;

  constructor(
  private configurationsService: ConfigurationsService,
  private permissionsService: PermissionsService,
  private urlService: UrlService,
  private crvtService: CRVTService,
  private changeDetectorRef: ChangeDetectorRef,
  public dialog: MatDialog
) {}

  ngOnInit(): void {
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.configurationsService.getConfigurations(this.blueprintId).subscribe(data => {
      this.configurationStates.forEach( configState => {
        data.forEach( config => {
          if(configState.configurationId === config.id){
            if(config?.manifest?.code) {
              configState.codeResult = config.manifest.code;
            }
            if(config?.manifest?.version) {
              const patchValue = parseInt(config.manifest.version.split('.').pop());
              if(patchValue > 0) {
              configState.hotfix = true;
            }
          }
          }
        })
      })
    });
    this.paginator.page
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(pageEvent => {
      this.applyPaginator();
    });

    this.filterRows();
  }

  filterRows(): void {
    // Remove all configuration states
    if (this.filteredConfigurationStates) {
      this.filteredConfigurationStates.splice(0, this.filteredConfigurationStates.length);
    }

    if (this.viewAllResults) {
      this.filteredConfigurationStates = [...this.configurationStates]
        .sort(this.propertyComparator('applicationFinishTime',SortDirection.DESC))
        .filter(configurationState => Constants.CONFIGURATION_FINAL_STATES.includes(configurationState.status));
    } else {
      let latestConfigurationState: ConfigurationState;
      for (const configurationState of this.configurationStates) {
        if (Constants.CONFIGURATION_FINAL_STATES.includes(configurationState.status)) {
          if (!latestConfigurationState || new Date(configurationState.applicationFinishTime).getTime()
            > new Date(latestConfigurationState.applicationFinishTime).getTime()) {
            latestConfigurationState = configurationState;
          }
        }
      }
      if (latestConfigurationState) {
        this.filteredConfigurationStates = [latestConfigurationState];
      }
    }

    if (this.searchTerm != null) {
      const searchBy = this.searchTerm.trim().toLocaleLowerCase();
      this.filteredConfigurationStates = this.filteredConfigurationStates.filter(configurationState =>
          configurationState.status?.toLocaleLowerCase().includes(searchBy)
          || this.configurationsMap[configurationState.configurationId].manifest.name.toLocaleLowerCase().includes(searchBy)
          || this.configurationsMap[configurationState.configurationId].manifest.version.toLocaleLowerCase().includes(searchBy));
    }
    this.paginator.length = this.filteredConfigurationStates.length;
  }

  expandRow(configurationState: ConfigurationState): void {
    this.expandedRows[configurationState.id] = !this.expandedRows[configurationState.id];
  }

  viewTestResults(configurationState: ConfigurationState, filename: string): void {
    const configuration = this.configurationsMap[configurationState.configurationId];
    this.crvtService.getViewResultsUrl(configuration.blueprintId, configurationState.instanceId, configurationState.id, filename)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((viewUrl) => {
      this.urlService.openUrlInNewTab(viewUrl);
    });
  }

  downloadTestResults(configurationState: ConfigurationState, filename: string): void {
    const configuration = this.configurationsMap[configurationState.configurationId];
    this.crvtService.downloadCRVT(configuration.blueprintId, configurationState.instanceId, configurationState.id, filename)
    .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  search(searchTerm?: string): void {
    this.searchTerm = searchTerm;
    this.filterRows();
  }

  doSort(property: string): void {
    if (!this.viewAllResults) {
      return;
    }

    if (!this.sort || this.sort.property !== property) {
      this.sort = {
        property,
        direction: SortDirection.ASC
      };
    } else if (this.sort.direction === SortDirection.ASC) {
      this.sort.direction = SortDirection.DESC;
    } else {
      this.sort = null;
    }

    if (!this.sort) {
      this.filteredConfigurationStates = this.filteredConfigurationStates.sort(this.propertyComparator('applicationFinishTime',
        SortDirection.DESC));
    } else if (this.sort.property === 'duration') {
      this.filteredConfigurationStates = this.filteredConfigurationStates.sort(this.durationComparator(this.sort.direction));
    } else {
      this.filteredConfigurationStates = this.filteredConfigurationStates.sort(this.propertyComparator(this.sort.property,
        this.sort.direction));
    }
    this.applyPaginator();
  }

  toggleViewAllResults(): void {
    this.viewAllResults = !this.viewAllResults;
    this.sort = null;
    this.filterRows();
  }

  private applyPaginator(): void {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    this.pageConfigurationStates = this.filteredConfigurationStates.slice(startIndex, startIndex + this.paginator.pageSize);
    this.changeDetectorRef.detectChanges();
  }

  private propertyComparator(property: string, direction: SortDirection): ComparatorFunc {
    return (configurationState1: ConfigurationState, configurationState2: ConfigurationState): number => {
      if (isDate(configurationState1[property])) {
        return direction === SortDirection.ASC
        ? configurationState1[property]?.getTime() - configurationState2[property]?.getTime()
        : configurationState2[property]?.getTime() - configurationState1[property]?.getTime();
      } else {
        if (configurationState1[property] && configurationState2[property]) {
          return direction === SortDirection.ASC
            ? configurationState1[property].localeCompare(configurationState2[property])
            : -(configurationState1[property].localeCompare(configurationState1[property]));
        }
      }
    };
  }

  private durationComparator(direction: SortDirection): ComparatorFunc {
    return (configurationState1: ConfigurationState, configurationState2: ConfigurationState): number => {
      if (configurationState1.applicationStartTime && configurationState1.applicationFinishTime
        && configurationState2.applicationStartTime && configurationState2.applicationFinishTime) {
          const duration1 = new Date(configurationState1.applicationFinishTime).getTime()
            - new Date(configurationState1.applicationStartTime).getTime();

          const duration2 = new Date(configurationState2.applicationFinishTime).getTime()
            - new Date(configurationState2.applicationStartTime).getTime();

          return direction === SortDirection.ASC ? duration1 - duration2 : duration2 - duration1;
      }
      return 0;
    };
  }

  configPackErrorDetails(packageError, titleName, jarName){
      this.dialog.open(ErrorDialogJarComponent, {
              panelClass: 'right-dialog-panel',
              disableClose: false,
              hasBackdrop: true,
              data: {
                title:  titleName,
                jarName: jarName,
                description: packageError,
              }
            });
  
    }
}
